import { useRef } from "react";
import { addMonths, format } from "date-fns";
import { useFloating, autoUpdate, offset, flip, shift, useDismiss, useInteractions, FloatingPortal, FloatingFocusManager } from "@floating-ui/react";
import { PiClockCounterClockwise, PiMinus, PiPlus, PiTrash } from "react-icons/pi";

import DateInput from "../DateInput";

const DEADLINE_PRESETS = [
  { label: "3 mois", months: 3 },
  { label: "6 mois", months: 6 },
  { label: "1 an", months: 12 },
];

const formatDeadline = (deadline) => {
  const date = new Date(deadline);
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
};

const RenewalPopover = ({ renewal, isOpen, onOpenChange, onRenewalChange }) => {
  const countInputRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  // Escape is handled on the panel itself: the admission scene binds Escape to "back to the list"
  const dismiss = useDismiss(context, { escapeKey: false });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const onCountChange = (count) => onRenewalChange({ ...renewal, count: count > 0 ? count : undefined });
  const onDeadlineChange = (deadline) => onRenewalChange({ ...renewal, deadline: deadline || undefined });

  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        title="Modifier le renouvellement"
        {...getReferenceProps({ onClick: () => onOpenChange(!isOpen) })}
        className={`flex items-center gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-xs transition-colors hover:bg-gray-100 hover:text-gray-900 ${isOpen ? "bg-gray-100 text-gray-900" : "text-gray-500"}`}
      >
        <PiClockCounterClockwise className="h-3.5 w-3.5 text-gray-400" />
        {renewal.count && <span className="font-medium tabular-nums text-gray-700">{renewal.count}×</span>}
        {renewal.deadline && <span>{formatDeadline(renewal.deadline)}</span>}
        {!renewal.count && !renewal.deadline && <span>Sans limite</span>}
      </button>

      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} initialFocus={countInputRef}>
            <div
              ref={refs.setFloating}
              style={{ ...floatingStyles, zIndex: 60 }}
              {...getFloatingProps()}
              data-modal="true"
              onKeyDown={(event) => {
                if (event.key !== "Escape") return;
                event.stopPropagation();
                onOpenChange(false);
              }}
              className="w-[272px] rounded-lg border border-gray-200 bg-white p-1 text-[13px] shadow-[0_8px_24px_rgba(16,24,40,0.12)]"
            >
              <div className="flex items-center justify-between px-2 py-1.5">
                <label htmlFor="renewalCount" className="text-gray-500">
                  Nombre
                </label>
                <div className="flex items-center rounded-md border border-gray-200">
                  <button
                    type="button"
                    aria-label="Diminuer"
                    onClick={() => onCountChange((renewal.count || 1) - 1)}
                    className="flex size-6 items-center justify-center rounded-l-md text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-900"
                  >
                    <PiMinus className="size-3" />
                  </button>
                  <input
                    ref={countInputRef}
                    id="renewalCount"
                    inputMode="numeric"
                    maxLength={2}
                    value={renewal.count ?? ""}
                    placeholder="∞"
                    onChange={(event) => {
                      const digits = event.target.value.replace(/\D/g, "");
                      onCountChange(digits ? parseInt(digits, 10) : undefined);
                    }}
                    className="w-8 bg-transparent text-center font-medium tabular-nums text-gray-900 outline-none placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    aria-label="Augmenter"
                    onClick={() => onCountChange((renewal.count || 0) + 1)}
                    className="flex size-6 items-center justify-center rounded-r-md text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-900"
                  >
                    <PiPlus className="size-3" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between px-2 py-0.5">
                <span className="text-gray-500">Jusqu&apos;au</span>
                <div className="w-[120px]" onFocus={(event) => event.target.select()}>
                  <DateInput ghost className="text-right" value={renewal.deadline ?? ""} onChange={(deadline) => deadline !== null && onDeadlineChange(deadline)} />
                </div>
              </div>

              <div className="flex gap-1 px-2 pb-2 pt-1">
                {[
                  ...DEADLINE_PRESETS.map((preset) => ({ label: preset.label, deadline: format(addMonths(new Date(), preset.months), "yyyy-MM-dd") })),
                  { label: "Sans limite", deadline: undefined },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => onDeadlineChange(preset.deadline)}
                    className={`rounded-md border px-1.5 py-0.5 text-xs transition-colors ${
                      renewal.deadline === preset.deadline ? "border-gray-300 bg-gray-50 text-gray-900" : "border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div className="my-1 h-px bg-gray-100" />

              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onRenewalChange({ enabled: false });
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                <PiTrash className="size-3.5 text-gray-400" />
                Retirer le renouvellement
              </button>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default RenewalPopover;
